import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  LoadingSpinner,
  PageContainer,
  toast,
} from '@startupquickstart/frontend';
import { AlertTriangle, ArrowLeft, RefreshCcw } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useDisconnectLinear,
  useLinearAuthStatus,
  useStartLinearAuth,
} from '@/api/support';

/**
 * Translate an OAuth callback `reason` code into a human-readable
 * explanation. Covers our own server-side reasons and Linear's
 * standard OAuth error codes (e.g. `access_denied`).
 */
function explainReason(reason: string | null): { title: string; body: string } {
  switch (reason) {
    case 'access_denied':
      return {
        title: 'You declined access on Linear',
        body: 'Linear reported that you cancelled the grant. You can try again — only read-and-write scopes for issues and comments are requested.',
      };
    case 'missing_code_or_state':
      return {
        title: 'Linear did not return the expected response',
        body: 'The callback was missing the OAuth code or state parameter. Try connecting again; if it keeps happening, check the OAuth application configuration.',
      };
    case 'invalid_state':
      return {
        title: 'The connect link expired or was tampered with',
        body: 'The signed state parameter could not be verified. This usually means the connect flow took longer than 10 minutes. Start a fresh attempt.',
      };
    case 'state_mismatch':
      return {
        title: 'State verification failed',
        body: 'A leftover state from an old connect attempt was sent back. Try again and complete the flow in one go.',
      };
    case 'exchange_failed':
      return {
        title: 'Linear refused our token request',
        body: 'We could not exchange the authorization code for an access token. Most often this means the OAuth client credentials on the server are wrong, or the redirect URI does not match what is registered in Linear.',
      };
    default:
      return {
        title: 'We could not connect Linear',
        body: reason
          ? `Linear or our server reported: \`${reason}\`. Try again, or reach out if this keeps happening.`
          : 'Something went wrong on the way back from Linear. Try again, or reach out if this keeps happening.',
      };
  }
}

/**
 * Linear OAuth landing page.
 *
 * Four concurrent jobs:
 *  - present a "Connect Linear" CTA when the user has no token,
 *  - render the post-callback success state (driven by `?status=connected`),
 *  - render a detailed error card on `?status=error&reason=…` so the user
 *    knows exactly what failed and how to retry,
 *  - let an already-connected user disconnect.
 */
export function ConnectLinearPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const callbackStatus = searchParams.get('status');
  const callbackReason = searchParams.get('reason');
  const isErrorState = callbackStatus === 'error';

  const { data: authStatus, isLoading } = useLinearAuthStatus();
  const startAuth = useStartLinearAuth();
  const disconnect = useDisconnectLinear();

  // Surface success via a toast and strip the params so it doesn't fire
  // again on refresh. Error states stay in the URL so the dedicated error
  // card can read `reason` and explain it — the user can dismiss with
  // "Try again" (which clears the params + restarts the flow) or "Back".
  useEffect(() => {
    if (callbackStatus === 'connected') {
      toast.success('Linear account connected');
      setSearchParams({}, { replace: true });
    }
  }, [callbackStatus, setSearchParams]);

  const handleConnect = async () => {
    // Clear any stale ?status=error params before kicking off a fresh flow.
    if (isErrorState) {
      setSearchParams({}, { replace: true });
    }
    try {
      const { authorizeUrl } = await startAuth.mutateAsync();
      window.location.assign(authorizeUrl);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not start Linear connect',
      );
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync();
      toast.success('Linear account disconnected');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not disconnect Linear',
      );
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  const errorInfo = isErrorState ? explainReason(callbackReason) : null;

  return (
    <PageContainer>
      <div className="mx-auto max-w-xl space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            Connect Linear
          </h1>
          <p className="text-muted-foreground">
            Support tickets you submit go straight into our Linear workspace.
            Connect your Linear account once so issues are filed under your
            name.
          </p>
        </div>

        {errorInfo && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{errorInfo.title}</AlertTitle>
            <AlertDescription>
              <p className="mb-3">{errorInfo.body}</p>
              {callbackReason && (
                <p className="mb-3 text-xs opacity-75">
                  <span className="font-mono">reason={callbackReason}</span>
                </p>
              )}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/internal/support')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Support
                </Button>
                <Button
                  size="sm"
                  onClick={handleConnect}
                  disabled={startAuth.isPending}
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  {startAuth.isPending ? 'Redirecting…' : 'Try again'}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>
              {authStatus?.connected ? 'Connected' : 'Not connected'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {authStatus?.connected ? (
              <>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Email</dt>
                  <dd>{authStatus.linearUserEmail}</dd>
                  <dt className="text-muted-foreground">Workspace</dt>
                  <dd className="font-mono text-xs">
                    {authStatus.linearWorkspaceId}
                  </dd>
                  <dt className="text-muted-foreground">Scope</dt>
                  <dd className="font-mono text-xs">{authStatus.scope}</dd>
                  {authStatus.expiresAt && (
                    <>
                      <dt className="text-muted-foreground">Token expires</dt>
                      <dd>
                        {new Date(authStatus.expiresAt).toLocaleString()}
                      </dd>
                    </>
                  )}
                </dl>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={handleDisconnect}
                    disabled={disconnect.isPending}
                  >
                    {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
                  </Button>
                  <Button onClick={() => navigate('/internal/support')}>
                    Go to Support
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm">
                  You'll be redirected to Linear to grant access. Only
                  read-and-write scopes for issues and comments are requested.
                </p>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                  <Button onClick={handleConnect} disabled={startAuth.isPending}>
                    {startAuth.isPending ? 'Redirecting…' : 'Connect Linear'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
